import type {
  LocationIqAutocompleteRow,
  PlaceAutocompleteSearchParams,
  PlaceAutocompleteSearchResult,
  PlaceSuggestion,
} from '@/lib/locations/locationiq.types'

/** @see https://docs.locationiq.com/reference/autocomplete-2 */
const LOCATIONIQ_AUTOCOMPLETE_URL = 'https://us1.locationiq.com/v1/autocomplete'

export const PLACE_AUTOCOMPLETE_MIN_CHARS = 2
export const PLACE_AUTOCOMPLETE_DEFAULT_LIMIT = 10
export const PLACE_AUTOCOMPLETE_DEBOUNCE_MS = 400

export class LocationIqAutocompleteError extends Error {
  constructor(
    message: string,
    readonly status?: number,
  ) {
    super(message)
    this.name = 'LocationIqAutocompleteError'
  }
}

function getApiKey(): string {
  const key = process.env.NEXT_PUBLIC_LOCATION_IQ_API_KEY?.trim()
  if (!key) {
    throw new LocationIqAutocompleteError('Missing NEXT_PUBLIC_LOCATION_IQ_API_KEY')
  }
  return key
}

function formatLabel(row: LocationIqAutocompleteRow): string {
  if (row.display_place && row.display_address) {
    return `${row.display_place}, ${row.display_address}`
  }
  return row.display_name
}

export function mapLocationIqRowToSuggestion(row: LocationIqAutocompleteRow): PlaceSuggestion {
  const lat = Number.parseFloat(row.lat)
  const lon = Number.parseFloat(row.lon)
  const address = row.address

  return {
    id: String(row.place_id),
    label: formatLabel(row),
    lat: Number.isFinite(lat) ? lat : 0,
    lon: Number.isFinite(lon) ? lon : 0,
    countryCode: address?.country_code?.toLowerCase(),
    city:
      address?.city ??
      address?.town ??
      address?.village ??
      address?.municipality ??
      address?.suburb ??
      undefined,
    state: address?.state,
    country: address?.country,
    raw: row,
  }
}

function normalizeQuery(q: string): string {
  return q.trim().replace(/\s+/g, ' ')
}

function cacheKey(params: PlaceAutocompleteSearchParams): string {
  const q = normalizeQuery(params.q).toLowerCase()
  const cc = (params.countrycodes ?? '').toLowerCase()
  const limit = params.limit ?? PLACE_AUTOCOMPLETE_DEFAULT_LIMIT
  const lang = (params.acceptLanguage ?? 'en').toLowerCase()
  const tag = params.tag ?? ''
  const layers = params.layers ?? ''
  return `${q}|${cc}|${limit}|${lang}|${tag}|${layers}`
}

/** In-memory LRU for the last N distinct autocomplete queries (client-only). */
const memoryCache = new Map<string, PlaceAutocompleteSearchResult>()
const MEMORY_CACHE_MAX = 20

function memoryCacheGet(key: string): PlaceAutocompleteSearchResult | undefined {
  const hit = memoryCache.get(key)
  if (!hit) return undefined
  memoryCache.delete(key)
  memoryCache.set(key, hit)
  return hit
}

function memoryCacheSet(key: string, value: PlaceAutocompleteSearchResult) {
  if (memoryCache.has(key)) memoryCache.delete(key)
  memoryCache.set(key, value)
  while (memoryCache.size > MEMORY_CACHE_MAX) {
    const oldest = memoryCache.keys().next().value
    if (oldest === undefined) break
    memoryCache.delete(oldest)
  }
}

/**
 * LocationIQ place autocomplete (browser). Uses `NEXT_PUBLIC_LOCATION_IQ_API_KEY`.
 * Results are cached in-memory (last 20 queries) and should be paired with TanStack Query in UI.
 */
export async function searchPlaceAutocomplete(
  params: PlaceAutocompleteSearchParams,
): Promise<PlaceAutocompleteSearchResult> {
  const q = normalizeQuery(params.q)
  if (q.length < PLACE_AUTOCOMPLETE_MIN_CHARS) {
    return { suggestions: [], query: q }
  }

  const key = cacheKey({ ...params, q })
  const cached = memoryCacheGet(key)
  if (cached) return cached

  const url = new URL(LOCATIONIQ_AUTOCOMPLETE_URL)
  url.searchParams.set('key', getApiKey())
  url.searchParams.set('q', q)
  url.searchParams.set('limit', String(Math.min(20, Math.max(1, params.limit ?? PLACE_AUTOCOMPLETE_DEFAULT_LIMIT))))
  url.searchParams.set('dedupe', '1')
  url.searchParams.set('normalizecity', '1')

  const lang = params.acceptLanguage?.trim() || 'en'
  url.searchParams.set('accept-language', lang)

  if (params.countrycodes?.trim()) url.searchParams.set('countrycodes', params.countrycodes.trim())
  if (params.tag?.trim()) url.searchParams.set('tag', params.tag.trim())
  if (params.layers?.trim()) url.searchParams.set('layers', params.layers.trim())

  const res = await fetch(url.toString(), {
    method: 'GET',
    headers: { Accept: 'application/json' },
  })

  if (!res.ok) {
    let detail = res.statusText
    try {
      const body = (await res.json()) as { error?: string }
      if (body?.error) detail = body.error
    } catch {
      /* ignore */
    }
    throw new LocationIqAutocompleteError(detail || 'Autocomplete request failed', res.status)
  }

  const rows = (await res.json()) as LocationIqAutocompleteRow[]
  const suggestions = Array.isArray(rows) ? rows.map(mapLocationIqRowToSuggestion) : []
  const result: PlaceAutocompleteSearchResult = { suggestions, query: q }

  memoryCacheSet(key, result)
  return result
}
